import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, X, Zap } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;
const MAJOR_THRESHOLD = 5;
const NOTABLE_THRESHOLD = 3;
const BANNER_DISMISS_MS = 12000;
const POLL_INTERVAL_MS = 60000;

const ASSET_NAMES = {
  BTCUSDT: 'Bitcoin', ETHUSDT: 'Ethereum', XRPUSDT: 'Ripple', ADAUSDT: 'Cardano', SOLUSDT: 'Solana',
  DJI: 'Dow Jones', IXIC: 'NASDAQ', SPX: 'S&P 500', FTSE: 'FTSE 100', GDAXI: 'DAX',
  GOLD: 'Gold', SILVER: 'Silver', OIL: 'Crude Oil', BRENT: 'Brent', NATGAS: 'Natural Gas',
  AAPL: 'Apple', TSLA: 'Tesla', AMZN: 'Amazon', MSFT: 'Microsoft', GOOGL: 'Alphabet', META: 'Meta',
};

const getSessionKey = (symbol) => `price_alert_${symbol}_${new Date().toDateString()}`;

const wasAlertShown = (symbol) => {
  try { return sessionStorage.getItem(getSessionKey(symbol)) === '1'; } catch { return false; }
};

const markAlertShown = (symbol) => {
  try { sessionStorage.setItem(getSessionKey(symbol), '1'); } catch {}
};

const formatPrice = (price) => {
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toFixed(2);
  return price.toFixed(4);
};

export const PriceAlertBanner = () => {
  const [majorAlert, setMajorAlert] = useState(null);
  const [visible, setVisible] = useState(false);

  const fetchAndCheck = useCallback(async () => {
    try {
      const endpoints = ['/api/crypto-prices', '/api/indices-prices', '/api/commodities-prices', '/api/stocks-prices'];
      const responses = await Promise.all(
        endpoints.map(ep =>
          fetch(`${BACKEND_URL}${ep}`, { signal: AbortSignal.timeout(10000) })
            .then(r => r.json()).catch(() => null)
        )
      );

      const movers = [];
      for (const res of responses) {
        if (!res?.success || !res.prices) continue;
        for (const [symbol, data] of Object.entries(res.prices)) {
          const pct = Math.abs(data.changePercent || 0);
          if (pct >= NOTABLE_THRESHOLD && !wasAlertShown(symbol)) {
            movers.push({
              symbol,
              name: ASSET_NAMES[symbol] || symbol,
              price: data.price,
              change: data.change || 0,
              changePercent: data.changePercent || 0,
              isMajor: pct >= MAJOR_THRESHOLD,
            });
          }
        }
      }

      movers.sort((a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent));

      const major = movers.find(m => m.isMajor);
      const notables = movers.filter(m => !m.isMajor);

      if (major) {
        markAlertShown(major.symbol);
        setMajorAlert(major);
        setVisible(true);
      }

      notables.slice(0, 3).forEach((m, i) => {
        markAlertShown(m.symbol);
        setTimeout(() => {
          const isUp = m.changePercent >= 0;
          toast(
            <div className="flex items-center gap-2" data-testid={`price-toast-${m.symbol}`}>
              <Zap className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="font-semibold">{m.name}</span>
              <span className="font-mono">${formatPrice(m.price)}</span>
              <span className={isUp ? 'text-emerald-500 font-semibold' : 'text-red-500 font-semibold'}>
                {isUp ? '+' : ''}{m.changePercent.toFixed(2)}%
              </span>
            </div>,
            { duration: 8000 }
          );
        }, i * 1500);
      });
    } catch (e) {
      console.error('Price alert fetch error:', e);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchAndCheck, 3000);
    const interval = setInterval(fetchAndCheck, POLL_INTERVAL_MS);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [fetchAndCheck]);

  useEffect(() => {
    if (!majorAlert) return;
    const timer = setTimeout(() => setVisible(false), BANNER_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [majorAlert]);

  if (!majorAlert || !visible) return null;

  const isUp = majorAlert.changePercent >= 0;

  return (
    <div
      data-testid="price-alert-banner"
      className="fixed top-0 left-0 right-0 z-[100] animate-in slide-in-from-top duration-500"
      style={{ animation: visible ? 'slideDown 0.5s ease-out' : 'slideUp 0.3s ease-in forwards' }}
    >
      <div className={`relative overflow-hidden ${isUp ? 'bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500' : 'bg-gradient-to-r from-red-600 via-red-500 to-orange-500'}`}>
        <div className="absolute inset-0 opacity-10 bg-[repeating-linear-gradient(90deg,transparent,transparent_10px,rgba(255,255,255,0.1)_10px,rgba(255,255,255,0.1)_20px)]" />
        <div className="relative flex items-center justify-center gap-3 px-4 py-2.5 text-white text-sm">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>

          <div className="flex items-center gap-2 font-medium">
            {isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            <span className="font-bold">{majorAlert.name}</span>
            <span className="font-mono font-bold">${formatPrice(majorAlert.price)}</span>
            <span className="px-1.5 py-0.5 rounded bg-white/20 font-bold text-xs">
              {isUp ? '+' : ''}{majorAlert.changePercent.toFixed(2)}%
            </span>
          </div>

          <a
            href="https://tdfxcapital.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="price-alert-trade-btn"
            className="ml-2 px-3 py-1 rounded-full bg-white text-xs font-bold transition-transform hover:scale-105"
            style={{ color: isUp ? '#059669' : '#dc2626' }}
          >
            Trade Now
          </a>

          <button
            onClick={() => setVisible(false)}
            data-testid="price-alert-dismiss"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceAlertBanner;
